/* ★ PAPARAN GAMBAR BESAR — tekan gambar produk, keluar saiz besar (arahan user 2026-09-13).
 *
 * Soalan user: "mcm mana user nak tengok gambar tambahan dan product secara besar?"
 * Sebelum ini borang Data Produk + jalur gambar kad Workspace hanya memapar petak 44–68px,
 * dan tiada satu pun cara membesarkannya. (Kad bergambar SATU sudah lama ada ikon kanta
 * sendiri — kod stabil itu TIDAK disentuh; komponen ini untuk tempat yang tiada.)
 *
 * Dipakai di 3 tempat:
 *   1. Borang Data Produk — gambar utama + gambar tambahan (anak panah lalu gambar 1 → 5)
 *   2. Galeri TikTok dalam borang yang sama — dengan butang "Pilih gambar ini" / "Buang pilihan"
 *   3. Jalur gambar pada kad Workspace (rupa v3.9)
 *
 * RUPA + NAVIGASI SAHAJA. Pemanggil memegang senarai gambar dan tindakan (prop `aksi`).
 *
 * ⚠️ Warna TETAP, bukan token: pemapar gambar sentiasa gelap tanpa mengira tema — gambar
 *    produk paling jelas di atas latar gelap, dan borang yang memanggilnya dikongsi dua rupa
 *    (rupa lama tiada token --s-*).
 * ⚠️ Teks lalu `T(kunci, lalai, vars)` PEMANGGIL (corak PopupRuangGambarAgent) — jangan
 *    panggil useT() di sini; rupa lama mesti kekal Melayu.
 * ⚠️ Kekunci ditangkap pada fasa CAPTURE di `window` dan dihentikan di situ. Tanpa itu Esc turut
 *    sampai kepada pendengar lain di belakang (cth LibraryView mengosongkan pilihan kad, borang
 *    di bawah tertutup) sedangkan pelanggan hanya mahu menutup gambar.
 * ⚠️ Gaya dalam PratontonGambar.css — awalan WAJIB `pgb-` (fail CSS global).
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './PratontonGambar.css';

const T_LALAI = (kunci, lalai) => lalai;

export default function PratontonGambar({ senarai, mula = 0, tajuk = '', aksi = null, onTutup, T: tProp }) {
    const T = tProp || T_LALAI;

    // Terima rentetan ATAU { src, tanda }. Entri tanpa alamat dibuang.
    const item = (Array.isArray(senarai) ? senarai : [])
        .map((x) => (typeof x === 'string' ? { src: x } : x))
        .filter((x) => x && typeof x.src === 'string' && x.src);
    const jumlah = item.length;

    const [pilih, setPilih] = useState(() => Math.max(0, Number(mula) || 0));
    // Diapit setiap render — senarai boleh memendek semasa pemapar terbuka.
    const i = Math.max(0, Math.min(pilih, jumlah - 1));
    const semasa = item[i];

    // Keadaan muat setiap gambar: tiada = sedang dimuat · 'siap' · 'ralat'.
    const [keadaan, setKeadaan] = useState({});

    const lapisRef = useRef(null);
    const sentuhRef = useRef(null);
    const tutupRef = useRef(onTutup);
    const gerakRef = useRef(null);

    const gerak = (arah) => setPilih((p) => {
        const s = Math.max(0, Math.min(p, jumlah - 1));
        const b = s + arah;
        return b < 0 || b > jumlah - 1 ? s : b;
    });

    // Pendengar kekunci dipasang SEKALI — nilai terkini dibaca melalui ref.
    useEffect(() => {
        tutupRef.current = onTutup;
        gerakRef.current = gerak;
    });

    useEffect(() => {
        const kekunci = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                tutupRef.current?.();
                return;
            }
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                e.stopPropagation();
                gerakRef.current?.(e.key === 'ArrowLeft' ? -1 : 1);
            }
        };
        window.addEventListener('keydown', kekunci, true);
        return () => window.removeEventListener('keydown', kekunci, true);
    }, []);

    // Fokus masuk ke pemapar (kekunci terus berfungsi), dipulangkan ke tempat asal bila tutup.
    useEffect(() => {
        const asal = document.activeElement;
        lapisRef.current?.focus({ preventScroll: true });
        return () => {
            if (asal && typeof asal.focus === 'function' && document.contains(asal)) {
                asal.focus({ preventScroll: true });
            }
        };
    }, []);

    // Muat gambar jiran lebih awal — tekan anak panah terus nampak gambar, bukan pusingan.
    const srcSebelum = item[i - 1]?.src || '';
    const srcSelepas = item[i + 1]?.src || '';
    useEffect(() => {
        [srcSebelum, srcSelepas].forEach((s) => {
            if (!s) return;
            const g = new Image();
            g.src = s;
        });
    }, [srcSebelum, srcSelepas]);

    if (!jumlah || typeof document === 'undefined') return null;

    const status = keadaan[semasa.src];
    const butang = typeof aksi === 'function' ? aksi(i, semasa) : null;
    const henti = (e) => e.stopPropagation();

    return createPortal(
        <div
            ref={lapisRef}
            className={butang ? 'pgb-lapis pgb-lapis--aksi' : 'pgb-lapis'}
            role="dialog"
            aria-modal="true"
            aria-label={T('gbrBesar.aria', 'Paparan gambar besar')}
            tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); onTutup?.(); }}
            onTouchStart={(e) => {
                const t0 = e.touches?.[0];
                sentuhRef.current = t0 ? { x: t0.clientX, y: t0.clientY } : null;
            }}
            onTouchEnd={(e) => {
                const a = sentuhRef.current;
                sentuhRef.current = null;
                const t1 = e.changedTouches?.[0];
                if (!a || !t1 || jumlah < 2) return;
                const dx = t1.clientX - a.x;
                const dy = t1.clientY - a.y;
                // Leret mendatar sahaja — leret menegak dibiarkan.
                if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) gerak(dx < 0 ? 1 : -1);
            }}
        >
            <div className="pgb-atas">
                {jumlah > 1 && <span className="pgb-kira">{i + 1} / {jumlah}</span>}
                {semasa.tanda && <span className="pgb-tanda">{semasa.tanda}</span>}
                {tajuk && <span className="pgb-tajuk" title={tajuk}>{tajuk}</span>}
                <span className="pgb-ruang" />
                <button
                    type="button"
                    className="pgb-bulat"
                    onClick={(e) => { e.stopPropagation(); onTutup?.(); }}
                    aria-label={T('gbrBesar.tutup', 'Tutup (Esc)')}
                    title={T('gbrBesar.tutup', 'Tutup (Esc)')}
                >
                    <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
                </button>
            </div>

            <div className="pgb-tengah">
                {jumlah > 1 && (
                    <button
                        type="button"
                        className="pgb-bulat pgb-anak pgb-anak--kiri"
                        disabled={i === 0}
                        onClick={(e) => { e.stopPropagation(); gerak(-1); }}
                        aria-label={T('gbrBesar.sebelum', 'Gambar sebelum (←)')}
                        title={T('gbrBesar.sebelum', 'Gambar sebelum (←)')}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: 24 }}>chevron_left</span>
                    </button>
                )}

                {status === 'ralat' ? (
                    <div className="pgb-ralat" onClick={henti}>
                        <span className="material-symbols-outlined" style={{ fontSize: 30 }}>broken_image</span>
                        <span>{T('gbrBesar.ralat', 'Gambar ini tidak dapat dipaparkan.')}</span>
                    </div>
                ) : (
                    <>
                        {status !== 'siap' && (
                            <div className="pgb-muat" aria-hidden><span className="pgb-pusing" /></div>
                        )}
                        <img
                            key={semasa.src}
                            src={semasa.src}
                            alt={tajuk || ''}
                            className="pgb-gambar"
                            style={{ opacity: status === 'siap' ? 1 : 0 }}
                            draggable={false}
                            onClick={henti}
                            onLoad={() => setKeadaan((k) => ({ ...k, [semasa.src]: 'siap' }))}
                            onError={() => setKeadaan((k) => ({ ...k, [semasa.src]: 'ralat' }))}
                        />
                    </>
                )}

                {jumlah > 1 && (
                    <button
                        type="button"
                        className="pgb-bulat pgb-anak pgb-anak--kanan"
                        disabled={i === jumlah - 1}
                        onClick={(e) => { e.stopPropagation(); gerak(1); }}
                        aria-label={T('gbrBesar.seterusnya', 'Gambar seterusnya (→)')}
                        title={T('gbrBesar.seterusnya', 'Gambar seterusnya (→)')}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: 24 }}>chevron_right</span>
                    </button>
                )}
            </div>

            {butang && (
                <div className="pgb-bawah">
                    <button
                        type="button"
                        className={butang.utama ? 'pgb-aksi pgb-aksi--utama' : 'pgb-aksi pgb-aksi--sekunder'}
                        disabled={!!butang.disabled}
                        onClick={(e) => { e.stopPropagation(); butang.onClick?.(); }}
                    >
                        {butang.ikon && <span className="material-symbols-outlined" style={{ fontSize: 17 }}>{butang.ikon}</span>}
                        {butang.label}
                    </button>
                    {butang.nota && <span className="pgb-nota" onClick={henti}>{butang.nota}</span>}
                </div>
            )}
        </div>,
        document.body,
    );
}
